import { useNode } from "@craftjs/core";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { VideoGridProps } from "../video-grid-component";
import { SortableVideoItem } from "./sorted-video-item";

const VideoOrderContainer = styled.div`
  height: max-content;
`;

import { useSelector } from "react-redux";

import { allRecordings } from "@/features/recordings/recordings-reducer";

const getIdsFromVideoList = (videoList: any[]): string[] => {
  return videoList.map(({ id }) => {
    return id;
  });
};

export const VideoOrderSettings = () => {
  const { t } = useTranslation();
  const {
    props,
    actions: { setProp },
  } = useNode((query) => {
    const filteredVideoList = query.props.videoList.filter((video) => {
      return typeof video !== "string";
    });
    return {
      props: {
        ...(query.props as VideoGridProps),
        videoList: filteredVideoList,
      },
    };
  });

  const recordings = useSelector(allRecordings);
  const [list, setList] = useState([]);

  const videos = useMemo(() => {
    return props.videoList.reduce((videos, currentVideo) => {
      if (currentVideo.kind === "recording") {
        const { recordingId } = currentVideo;
        const recording = recordings.find(
          (recording) => recording.id === recordingId
        );
        if (recording) {
          videos.push(recording);
        }
      } else {
        videos.push(currentVideo);
      }
      return videos;
    }, []);
  }, [props.videoList, recordings]);

  useEffect(() => {
    setList(getIdsFromVideoList(videos));
  }, [videos]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    if (active.id === over.id) {
      return;
    }

    const activeIndex = list.findIndex((item) => item === active.id);
    const overIndex = list.findIndex((item) => item === over.id);
    const newList = arrayMove(list, activeIndex, overIndex);

    setList(newList);
    setProp((props) => {
      props.videoList = newList.map((videoId) => {
        return props.videoList.find((video) => {
          return video.id === videoId || video.recordingId === videoId;
        });
      });
    });
  };

  // Remove video from video order settings
  const removeVideo = (videoIndex: number) => {
    const newList = list.filter((__, index) => index !== videoIndex);

    setList(newList);
    setProp((props) => {
      props.videoList = newList.map((videoId) => {
        return props.videoList.find((video) => {
          return video.id === videoId || video.recordingId === videoId;
        });
      });
    });
  };

  if (!list && list?.length === 0) {
    return null;
  }

  return (
    <VideoOrderContainer>
      <span className="mb-2 block text-sm font-medium leading-6">
        {t("preference-panel.video-order")}
      </span>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={list} strategy={verticalListSortingStrategy}>
          {videos.map((video, index) => (
            <SortableVideoItem
              key={video.id}
              video={video}
              deleteVideo={() => {
                removeVideo(index);
              }}
            />
          ))}
        </SortableContext>
      </DndContext>
    </VideoOrderContainer>
  );
};
